import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { currencySymbol, doctorCurrencySymbol, formatMoney, sharedDoctorCurrencySymbol } from '../../utils/currency';

// Each row is priced in its own doctor's currency (the hospital's `currency`
// when the parent passes one) — never a guessed € (P24).

// Admin: top doctors for the period, sortable by any numeric column (client-side).
const DoctorsLeaderboard = ({ leaderboard, loading, currency }) => {
  const { t } = useTranslation('dashboard');
  const [sortKey, setSortKey] = useState('revenue');
  const rows = Array.isArray(leaderboard?.data) ? [...leaderboard.data] : [];
  const signFor = (r) => (currency ? currencySymbol(currency) : doctorCurrencySymbol(r));
  const money = (n, r) => formatMoney(n, signFor(r));
  const perHour = (n, r) => formatMoney(n, signFor(r), { suffix: '/h' });
  const headerSign = currency ? currencySymbol(currency) : sharedDoctorCurrencySymbol(rows);
  rows.sort((a, b) => (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0));

  const cols = [
    { key: 'appointments', label: t('dashboard.appointments_tab', 'Appts') },
    { key: 'completed', label: t('dashboard.completed', 'Completed') },
    { key: 'noShows', label: t('dashboard.missed', 'No-show') },
    { key: 'patients', label: t('dashboard.patients_col', 'Patients') },
    { key: 'occupancy', label: t('dashboard.occupancy_col', 'Occ. %') },
    { key: 'ehr', label: headerSign ? t('dashboard.ehr_col_sign', { sign: headerSign, defaultValue: 'EHR {{sign}}/h' }) : t('dashboard.ehr_col_plain', 'EHR /h') },
    { key: 'revenue', label: t('dashboard.revenue_tab', 'Revenue') },
  ];

  return (
    <div className="bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md" data-testid="admin-leaderboard">
      <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center mb-3">
        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
        {t('dashboard.top_doctors', 'Top doctors')}
      </h3>
      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.loading', 'Loading...')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.no_data', 'No data for this period')}</p>
      ) : (
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">{t('dashboard.doctor', 'Doctor')}</th>
                <th className="py-2 pr-3">{t('dashboard.country', 'Country')}</th>
                {cols.map((c) => (
                  <th key={c.key} className="py-2 pl-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSortKey(c.key)}
                      className={`hover:text-[#3AA8A8] ${sortKey === c.key ? 'text-[#3AA8A8] font-bold' : ''}`}
                    >
                      {c.label}{sortKey === c.key ? ' ↓' : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.doctorId} data-testid={`leaderboard-row-${r.doctorId}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium text-gray-800">{r.name}</td>
                  <td className="py-2 pr-3 text-gray-500">{r.country}</td>
                  <td className="py-2 pl-3 text-right text-gray-700">{r.appointments}</td>
                  <td className="py-2 pl-3 text-right text-emerald-600">{r.completed}</td>
                  <td className="py-2 pl-3 text-right text-amber-600">{r.noShows}</td>
                  <td className="py-2 pl-3 text-right text-gray-700">{r.patients ?? 0}</td>
                  <td className="py-2 pl-3 text-right text-gray-700">{r.occupancy ?? 0}%</td>
                  <td className="py-2 pl-3 text-right font-semibold text-[#3AA8A8]" title={t('dashboard.ehr_tip', 'Effective Hourly Revenue = revenue ÷ completed hours')}>{perHour(r.ehr, r)}</td>
                  <td className="py-2 pl-3 text-right font-semibold text-gray-800">{money(r.revenue, r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DoctorsLeaderboard;
